import cx from 'classnames';
import * as React from 'react';

import { ButtonColorUnion } from 'components/ButtonDeprecated';
import Image from 'components/Image';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants, { FooterLinks } from 'constants/LinkConstants';
import InstagramIcon from './icons/InstagramIcon';
import TwitterIcon from './icons/TwitterIcon';
import FacebookIcon from './icons/FacebookIcon';
import YouTubeIcon from './icons/YouTubeIcon';

import './Footer.scss';

type FooterNavColumnLinks = {
  color?: ButtonColorUnion;
  copy: string;
  external?: boolean;
  image?: string;
  to: string;
};

type FooterNavColumnType = {
  className?: string;
  links: Array<FooterNavColumnLinks>;
  title?: string;
};

const FooterNavColumn = ({ className, links, title }: FooterNavColumnType) => (
  <section className={cx('Footer-nav-column', className)}>
    {title && (
      <Text element="h4" className="Footer-nav-column-title">
        {title}
      </Text>
    )}
    <ul>
      {links.map((item) => (
        <li key={item.to} className="Footer-nav-list-item">
          <Link href={item.to} color={item.color} target={item.external ? '_blank' : undefined}>
            <Text size="medium">
              {item.image && <Image src={item.image} className="Footer-nav-list-item-icon" />}
              {item.copy}
            </Text>
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

type Props = {
  disclaimer?: React.ReactNode;
};

export default function Footer({ disclaimer }: Props) {
  const socials = [
    {
      name: 'Facebook',
      image: require('assets/images/Footer/socials/facebook.svg'),
      to: LinkConstants.FACEBOOK,
    },
    {
      name: 'Instagram',
      image: require('assets/images/Footer/socials/instagram.svg'),
      to: LinkConstants.INSTAGRAM,
    },
    {
      name: 'YouTube',
      image: require('assets/images/Footer/socials/youtube.svg'),
      to: LinkConstants.YOUTUBE,
    },
    {
      name: 'Twitter',
      image: require('assets/images/Footer/socials/twitter.svg'),
      to: LinkConstants.TWITTER,
    },
  ];

  const iKeepSafe = (
    <Text size="xsmall" weight="semibold" wrap={false} className="Footer-ikeepsafe">
      Certified by
      <Image
        src={require('assets/images/Footer/ikeepsafe.svg')}
        alt="iKeepSafe"
        className="Footer-ikeepsafe-mascot"
      />
    </Text>
  );

  return (
    <footer className="Footer">
      {disclaimer && (
        <div className="Footer-disclaimer Footer-container">
          <Row>
            <Text color="graphite" element="p" flush size="xxxsmall">
              {disclaimer}
            </Text>
          </Row>
        </div>
      )}
      <div className="Footer-container--main Footer-container">
        <nav className="Footer-nav">
          <section className="Footer-nav-column">
            <div>
              <Link className="Footer-brandLink" href={LinkConstants.MARKETING}>
                Remind
              </Link>
            </div>
            <div className="Footer-socials">
              <a href={LinkConstants.FACEBOOK} target="_blank" rel="noreferrer">
                <FacebookIcon />
              </a>
              <a href={LinkConstants.INSTAGRAM} target="_blank" rel="noreferrer">
                <InstagramIcon />
              </a>
              <a href={LinkConstants.YOUTUBE} target="_blank" rel="noreferrer">
                <YouTubeIcon />
              </a>
              <a href={LinkConstants.TWITTER} target="_blank" rel="noreferrer">
                <TwitterIcon />
              </a>
            </div>
          </section>
          <div>
            <FooterNavColumn
              title="Products"
              links={[
                {
                  copy: 'Chat',
                  to: LinkConstants.MOBILE_WEB_CHAT_LINK,
                },
                {
                  copy: 'Hub',
                  to: FooterLinks.HUB,
                },
              ]}
            />
            <FooterNavColumn
              title="Download the app"
              links={[
                {
                  copy: 'iOS - Apple App Store',
                  to: FooterLinks.APP_STORE,
                },
                {
                  copy: 'Android - Google Play Store',
                  to: FooterLinks.GOOGLE_PLAY,
                },
              ]}
            />
          </div>
          <FooterNavColumn
            title="Who it’s for"
            links={[
              {
                copy: 'Teachers',
                to: LinkConstants.TEACHERS,
              },
              {
                copy: 'Administrators',
                to: FooterLinks.ADMINISTRATORS,
              },
              {
                copy: 'Higher Education',
                to: FooterLinks.HIGHER_EDUCATION,
              },
              {
                copy: 'Students',
                to: FooterLinks.FAMILIES,
              },
            ]}
          />
          <FooterNavColumn
            title="Resources"
            links={[
              {
                copy: 'Resources Library',
                to: LinkConstants.RESOURCES,
              },
              {
                copy: 'Help Center',
                to: FooterLinks.HELP_CENTER,
              },
              {
                copy: 'Remind Blog',
                to: FooterLinks.BLOG,
              },
              {
                copy: 'Contact support',
                to: FooterLinks.CONTACT_SUPPORT,
              },
              {
                copy: 'Contact sales',
                to: FooterLinks.CONTACT_SALES,
              },
              {
                copy: 'System status',
                to: FooterLinks.SYSTEM_STATUS,
              },
            ]}
          />
          <FooterNavColumn
            title="Company"
            links={[
              {
                copy: 'About us',
                to: LinkConstants.ABOUT,
              },
              {
                copy: 'Careers',
                to: FooterLinks.CAREERS,
              },
              {
                copy: 'Press',
                to: FooterLinks.PRESS,
              },
              {
                copy: 'Partners',
                to: FooterLinks.PARTNERS,
              },
              {
                copy: 'Terms and policies',
                to: FooterLinks.TERMS_OF_SERVICE,
              },
              {
                copy: 'Trust and safety',
                to: FooterLinks.TRUST_SAFETY,
              },
            ]}
          />
          <section className="Footer-nav-column">
            <img
              src={require('assets/images/Footer/ikeep-safe.png')}
              alt="ikeep-safe"
              style={{
                width: 141,
              }}
            />
          </section>
        </nav>
      </div>
    </footer>
  );
}
